import {
  Api,
  hasPermission,
  IUser,
  ISuggestion,
  NotificationType,
  PERMISSIONS,
} from "@model";
import { SearchBar, ISearchQuery } from "@components";
import { useIsCompact } from "../../hooks/useIsCompact";
import {
  Avatar,
  Table,
  Tabs,
  Tag,
  Dropdown,
  Menu,
  Modal,
  Checkbox,
  Button,
  message,
  Input,
  Select,
  Form,
  Tooltip,
  Empty,
  Pagination,
  Spin,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { MoreOutlined } from "@ant-design/icons";
import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router";
import { ColumnsType } from "antd/lib/table";
import { Link, useSearchParams } from "react-router-dom";

const avatarSrc = (avatar?: string) =>
  avatar && avatar.startsWith("http") ? avatar : `/${avatar}_sm.jpg`;

const formatLastRun = (value?: string | Date | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return {
    short: `${day} ${hours}:${minutes}`,
    full: date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const suggestionPerson = (user: string | IUser | undefined) => {
  if (user && typeof user === "object") {
    return {
      label: user.name || user.email || "-",
      avatar: avatarSrc(user.avatar),
    };
  }
  return { label: typeof user === "string" ? user : "-", avatar: undefined };
};

const UserListPage = () => {
  const isCompact = useIsCompact();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [fcmNotExpired, setFcmNotExpired] = useState(false);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();

  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsPageSize, setSuggestionsPageSize] = useState(200);
  const [suggestionsCurrentPage, setSuggestionsCurrentPage] = useState(1);
  const [suggestionsTotal, setSuggestionsTotal] = useState(0);

  const [betaFeaturesModalVisible, setBetaFeaturesModalVisible] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);
  const [userBetaFeatures, setUserBetaFeatures] = useState<
    Record<string, boolean>
  >({});
  const [betaFeaturesLoading, setBetaFeaturesLoading] = useState(false);

  const [notificationModalVisible, setNotificationModalVisible] =
    useState(false);
  const [notificationUser, setNotificationUser] = useState<IUser | null>(null);
  const [notificationForm] = Form.useForm();
  const [notificationLoading, setNotificationLoading] = useState(false);
  const canViewUsers = hasPermission(PERMISSIONS.usersView);
  const canDeleteUsers = hasPermission(PERMISSIONS.usersDelete);
  const canViewUserMessages = hasPermission(PERMISSIONS.usersMessages);
  const canEditBetaFeatures = hasPermission(PERMISSIONS.usersBetaFeatures);
  const canSendUserNotification = hasPermission(
    PERMISSIONS.usersSendNotification,
  );

  const syncStateFromUrl = useCallback(() => {
    const q = searchParams.get("q") ?? "";
    setSearchQuery((prev) => (prev.q !== q ? { q } : prev));
    setCurrentPage(Math.max(1, Number(searchParams.get("page")) || 1));
    const ps = Number(searchParams.get("pageSize"));
    if (ps) setPageSize(Math.min(100, Math.max(10, ps)));
    const fcm = searchParams.get("fcmNotExpired");
    setFcmNotExpired(fcm === "1" || fcm === "true");
    const sf = searchParams.get("sortField");
    setSortField(sf || undefined);
    const so = searchParams.get("sortOrder");
    setSortOrder(so || undefined);
  }, [searchParams]);

  useEffect(() => {
    syncStateFromUrl();
  }, [syncStateFromUrl]);

  const updateUrl = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const fetchUsers = useCallback(
    (pagination?: any, sorter?: any) => {
      setLoading(true);
      const page = pagination?.current ?? currentPage;
      const size = pagination?.pageSize ?? pageSize;

      if (pagination?.current !== undefined) setCurrentPage(pagination.current);
      if (pagination?.pageSize !== undefined) setPageSize(pagination.pageSize);

      const sorterData =
        sorter?.field != null
          ? {
              field: sorter.field,
              columnKey: sorter.columnKey,
              order: sorter.order,
            }
          : sortField != null
            ? {
                field: sortField,
                columnKey: sortField,
                order: sortOrder === "asc" ? "ascend" : "descend",
              }
            : undefined;

      Api.post(`/api/admin/panel/users/list`, {
        current: page,
        pageSize: size,
        q: searchQuery.q,
        fcmNotExpired: fcmNotExpired || undefined,
        sorter: sorterData,
      })
        .then((response) => {
          const data = response.data.data;
          const list = Array.isArray(data) ? data : (data?.list ?? []);
          const total = typeof data?.total === "number" ? data.total : null;
          setUsers(list);
          setTotalCount(total);
          setLoading(false);
          if (total != null) {
            setTotalPages(Math.ceil(total / size) || 1);
          } else {
            const n = list.length;
            setTotalPages(n < size ? page : page + 1);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          setLoading(false);
        });
    },
    [searchQuery.q, currentPage, pageSize, sortField, sortOrder, fcmNotExpired],
  );

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery.q,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    fcmNotExpired,
  ]);

  const navigate = useNavigate();

  const handleOpenBetaFeaturesModal = useCallback(async (user: IUser) => {
    setSelectedUser(user);
    setBetaFeaturesModalVisible(true);
    setBetaFeaturesLoading(true);

    // Use betaFeatures from user object
    setUserBetaFeatures(user.betaFeatures || {});

    try {
      // Fetch available features from settings
      const featuresResponse = await Api.get("/api/settings/features");
      const features = featuresResponse.data.data || [];
      if (Array.isArray(features)) {
        setAvailableFeatures(features);
      } else if (typeof features === "string") {
        setAvailableFeatures(
          features
            .split("\n")
            .map((f) => f.trim())
            .filter((f) => f.length > 0),
        );
      } else {
        setAvailableFeatures([]);
      }
    } catch (settingsError: any) {
      if (settingsError.response?.status === 404) {
        message.warning(
          "Features setting not found. Please create a 'features' setting first.",
        );
      }
      setAvailableFeatures([]);
    } finally {
      setBetaFeaturesLoading(false);
    }
  }, []);

  const handleBetaFeatureToggle = (featureName: string, checked: boolean) => {
    setUserBetaFeatures((prev) => ({
      ...prev,
      [featureName]: checked,
    }));
  };

  const handleSaveBetaFeatures = async () => {
    if (!selectedUser) return;

    try {
      await Api.put(
        `/api/admin/panel/users/${selectedUser._id}/beta-features`,
        {
          betaFeatures: userBetaFeatures,
        },
      );
      message.success("Beta features updated successfully");
      setBetaFeaturesModalVisible(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating beta features:", error);
      message.error("Failed to update beta features");
    }
  };

  const handleOpenNotificationModal = useCallback(
    (user: IUser) => {
      setNotificationUser(user);
      setNotificationModalVisible(true);
      notificationForm.resetFields();
    },
    [notificationForm],
  );

  const handleSendNotification = async () => {
    if (!notificationUser) return;

    try {
      const values = await notificationForm.validateFields();
      setNotificationLoading(true);
      await Api.put(
        `/api/admin/panel/users/${notificationUser._id}/send-notification`,
        {
          title: values.title,
          body: values.body,
          notificationType: values.notificationType,
        },
      );
      message.success("Notification sent successfully");
      setNotificationModalVisible(false);
      setNotificationUser(null);
      notificationForm.resetFields();
    } catch (error: any) {
      console.error("Error sending notification:", error);
      if (error.errorFields) {
        return;
      }
      message.error(
        error.response?.data?.message || "Failed to send notification",
      );
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleDeleteUser = useCallback((user: IUser) => {
    Modal.confirm({
      title: "Delete User",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete user "${
        user.name || user.email
      }"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete(`/api/admin/panel/users/${user._id}`);
          message.success("User deleted successfully");
          setUsers((prevUsers) => prevUsers.filter((u) => u._id !== user._id));
        } catch (error) {
          console.error("Error deleting user:", error);
          message.error("Failed to delete user");
        }
      },
    });
  }, []);

  const handleDeleteUserPermanently = useCallback((user: IUser) => {
    Modal.confirm({
      title: "Delete User Permanently",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to PERMANENTLY delete user "${
        user.name || user.email
      }"? This will delete the user and ALL related data (messages, notifications, chats, connections, etc.). This action CANNOT be undone!`,
      okText: "Delete Permanently",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete(`/api/admin/panel/users/${user._id}/permanent`);
          message.success("User permanently deleted successfully");
          setUsers((prevUsers) => prevUsers.filter((u) => u._id !== user._id));
        } catch (error) {
          console.error("Error permanently deleting user:", error);
          message.error("Failed to permanently delete user");
        }
      },
    });
  }, []);

  const getLanguageCode = (lang: string | undefined): string => {
    if (!lang) return "-";
    return lang.split("-")[0];
  };

  const renderUserActions = useCallback(
    (record: IUser) => {
      const menu = (
        <Menu>
          <Menu.Item
            key="profile"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              navigate(`/panel/users/edit/${record._id}`, {
                state: record,
              });
            }}
          >
            profile
          </Menu.Item>
          {canViewUserMessages && (
            <Menu.Item
              key="messages"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                navigate(`/panel/users/messages/${record._id}`);
              }}
            >
              View Messages
            </Menu.Item>
          )}
          {canEditBetaFeatures && (
            <Menu.Item
              key="beta-features"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                handleOpenBetaFeaturesModal(record);
              }}
            >
              Beta Features
            </Menu.Item>
          )}
          {canSendUserNotification && (
            <Menu.Item
              key="send-notification"
              onClick={(e) => {
                e.domEvent.stopPropagation();
                handleOpenNotificationModal(record);
              }}
            >
              Send Notification
            </Menu.Item>
          )}
          {canDeleteUsers && (
            <Menu.Item
              key="delete"
              danger
              onClick={(e) => {
                e.domEvent.stopPropagation();
                handleDeleteUser(record);
              }}
            >
              Delete User
            </Menu.Item>
          )}
          {canDeleteUsers && (
            <Menu.Item
              key="delete-permanent"
              danger
              onClick={(e) => {
                e.domEvent.stopPropagation();
                handleDeleteUserPermanently(record);
              }}
            >
              Delete Permanently
            </Menu.Item>
          )}
        </Menu>
      );

      return (
        <Dropdown overlay={menu} trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        </Dropdown>
      );
    },
    [
      navigate,
      handleOpenBetaFeaturesModal,
      handleDeleteUser,
      handleDeleteUserPermanently,
      handleOpenNotificationModal,
      canDeleteUsers,
      canEditBetaFeatures,
      canSendUserNotification,
      canViewUserMessages,
    ],
  );

  const columns = useMemo(
    (): ColumnsType<any> =>
      [
        {
          title: (
            <Tooltip title="Name">
              <span>Name</span>
            </Tooltip>
          ),
          dataIndex: "name",
          key: "name",
          width: 150,
          onCell: () => ({ style: { maxWidth: 72, width: 72 } }),
          render: (name: string, record: IUser) => {
            const label = name || "-";
            const nameStyle: CSSProperties = {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flex: 1,
            };
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  maxWidth: 64,
                  minWidth: 0,
                }}
              >
                <Avatar
                  size={22}
                  src={
                    record.avatar && record.avatar.startsWith("http")
                      ? record.avatar
                      : `/${record.avatar}_sm.jpg`
                  }
                />
                <Tooltip title={label}>
                  {canViewUsers ? (
                    <Link
                      to={`/panel/users/edit/${record._id}`}
                      state={record}
                      onClick={(e) => e.stopPropagation()}
                      style={nameStyle}
                    >
                      {label}
                    </Link>
                  ) : (
                    <span style={nameStyle}>{label}</span>
                  )}
                </Tooltip>
              </div>
            );
          },
        },
        {
          title: (
            <Tooltip title="Email">
              <span>Email</span>
            </Tooltip>
          ),
          dataIndex: "email",
          key: "email",
          render: (email: string) => {
            if (!email) return "-";
            const at = email.indexOf("@");
            if (at <= 0) return email;
            const local = email.slice(0, at);
            const domain = email.slice(at + 1);
            const brief = `${local.slice(0, 4)}..@${domain.slice(0, 5) || ""}..`;
            return (
              <Tooltip title={email}>
                <span>{brief}</span>
              </Tooltip>
            );
          },
        },
        {
          title: (
            <Tooltip title="FCM token expired (e.g. app deleted)">
              <span>FCM</span>
            </Tooltip>
          ),
          dataIndex: "fcmExpired",
          key: "fcmExpired",
          width: 72,
          render: (v: boolean) =>
            v ? <Tag color="orange">Expired</Tag> : <Tag color="green">OK</Tag>,
        },
        {
          title: (
            <Tooltip title="Country/City">
              <span>C/C</span>
            </Tooltip>
          ),
          key: "location",
          width: 100,
          onCell: () => ({ style: { maxWidth: 72, width: 72 } }),
          render: (_: any, row: IUser) => {
            const location = [row.country, row.city].filter(Boolean).join(", ");
            const label = location || "-";
            return (
              <Tooltip title={label}>
                <span
                  style={{
                    display: "block",
                    maxWidth: 64,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              </Tooltip>
            );
          },
        },
        {
          title: (
            <Tooltip title="Register Date">
              <span>Reg. Date</span>
            </Tooltip>
          ),
          dataIndex: "registerDate",
          key: "registerDate",

          render: (w: any) => (w ? new Date(w).toLocaleDateString() : "-"),
        },
        {
          title: (
            <Tooltip title="Last time the user opened the app">
              <span>Last run</span>
            </Tooltip>
          ),
          dataIndex: "lastRunTime",
          key: "lastRunTime",
          sorter: true,
          render: (w: any) => {
            const lastRun = formatLastRun(w);
            if (!lastRun) return "-";
            return (
              <Tooltip title={lastRun.full}>
                <span>{lastRun.short}</span>
              </Tooltip>
            );
          },
        },
        {
          title: (
            <Tooltip title="Language (origin/target)">
              <span>Lang</span>
            </Tooltip>
          ),
          key: "language",
          render: (_: any, row: IUser) => {
            const origin = getLanguageCode(row.originLanguage);
            const target = getLanguageCode(row.targetLanguage);
            return (
              <div>
                <span>
                  {origin}\{target}
                </span>
              </div>
            );
          },
        },
        {
          title: (
            <Tooltip title="Daily Messages">
              <span>DM</span>
            </Tooltip>
          ),
          dataIndex: "dailyUsedMessagesCount",
          key: "dailyUsedMessagesCount",
          render: (w: number) => w ?? 0,
        },
        {
          title: (
            <Tooltip title="Total Messages">
              <span>TM</span>
            </Tooltip>
          ),
          dataIndex: "totalMessagesCount",
          key: "totalMessagesCount",
          render: (w: number) => w ?? 0,
        },
        {
          title: (
            <Tooltip title="Daily Partner Find">
              <span>DPF</span>
            </Tooltip>
          ),
          dataIndex: "dailyPartnerFindCount",
          key: "dailyPartnerFindCount",
          render: (w: number) => w ?? 0,
        },
        {
          title: (
            <Tooltip title="Version">
              <span>Ver</span>
            </Tooltip>
          ),
          dataIndex: "installedVersion",
          key: "installedVersion",
          sorter: true,
        },
        {
          title: (
            <Tooltip title="Runtimes">
              <span>Runs</span>
            </Tooltip>
          ),
          dataIndex: "runTimes",
          key: "runTimes",
          render: (w: number) => w ?? 0,
        },
        {
          title: (
            <Tooltip title="Installed From">
              <span>From</span>
            </Tooltip>
          ),
          dataIndex: "installedFrom",
          key: "installedFrom",
          render: (w: string) => w || "-",
        },
        {
          title: (
            <Tooltip title="Following/Followers">
              <span>FF</span>
            </Tooltip>
          ),
          key: "friends",
          render: (_: any, row: IUser) => {
            return (
              <span>
                {row.followingCount ?? 0}/{row.followersCount ?? 0}
              </span>
            );
          },
        },
        {
          title: (
            <Tooltip title="Beta Features">
              <span>Beta</span>
            </Tooltip>
          ),
          key: "betaFeatures",
          render: (_: any, record: IUser) => {
            const enabledFeatures = record.betaFeatures
              ? Object.entries(record.betaFeatures)
                  .filter(([_, enabled]) => enabled === true)
                  .map(([featureName]) => featureName)
              : [];

            if (enabledFeatures.length === 0) {
              return null;
            }

            return (
              <div>
                {enabledFeatures.map((feature) => (
                  <div key={feature} style={{ marginBottom: "4px" }}>
                    {feature}
                  </div>
                ))}
              </div>
            );
          },
        },
        {
          title: "Actions",
          key: "actions",
          render: (_: any, record: IUser) => renderUserActions(record),
        },
      ].filter((a) => a.key),
    [canViewUsers, renderUserActions],
  );

  useEffect(() => {
    setSuggestionsLoading(true);
    Api.get(`/api/admin/panel/suggestions`, {
      params: {
        page: suggestionsCurrentPage,
        limit: suggestionsPageSize,
      },
    })
      .then((response) => {
        setSuggestions(response.data.data.suggestions || []);
        setSuggestionsTotal(response.data.data.total || 0);
        setSuggestionsLoading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setSuggestionsLoading(false);
      });
  }, [suggestionsCurrentPage, suggestionsPageSize]);

  const handleSuggestionsPaginationChange = (
    page: number,
    pageSize?: number,
  ) => {
    setSuggestionsCurrentPage(page);
    if (pageSize) setSuggestionsPageSize(pageSize);
  };

  const formatDate = (date: any): string => {
    if (!date) return "-";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const suggestionsColumns = useMemo(
    (): ColumnsType<any> =>
      [
        {
          title: "For User",
          key: "forUser",
          render: (_: any, row: ISuggestion) => {
            const user =
              typeof row.forUserID === "object" ? row.forUserID : null;
            return (
              <div>
                {user && (
                  <>
                    <Avatar
                      src={
                        user.avatar && user.avatar.startsWith("http")
                          ? user.avatar
                          : `/${user.avatar}_sm.jpg`
                      }
                      style={{ marginRight: 8 }}
                    />
                    <span>{user.name || user.email || "-"}</span>
                  </>
                )}
                {!user && (
                  <span>
                    {typeof row.forUserID === "string" ? row.forUserID : "-"}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          title: "Suggested User",
          key: "suggestedUser",
          render: (_: any, row: ISuggestion) => {
            const user =
              typeof row.suggestedUserID === "object"
                ? row.suggestedUserID
                : null;
            return (
              <div>
                {user && (
                  <>
                    <Avatar
                      src={
                        user.avatar && user.avatar.startsWith("http")
                          ? user.avatar
                          : `/${user.avatar}_sm.jpg`
                      }
                      style={{ marginRight: 8 }}
                    />
                    <span>{user.name || user.email || "-"}</span>
                  </>
                )}
                {!user && (
                  <span>
                    {typeof row.suggestedUserID === "string"
                      ? row.suggestedUserID
                      : "-"}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          title: "Created Date",
          dataIndex: "createdAt",
          key: "createdAt",
          sorter: (a: ISuggestion, b: ISuggestion) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateA - dateB;
          },
          render: (w: any) => formatDate(w),
        },
        {
          title: "Scheduled At",
          dataIndex: "scheduledAt",
          key: "scheduledAt",
          render: (w: any) => formatDate(w),
        },
        {
          title: "Process State",
          dataIndex: "isProcessed",
          key: "isProcessed",
          render: (w: boolean) => (
            <Tag color={w ? "green" : "orange"}>
              {w ? "Processed" : "Pending"}
            </Tag>
          ),
        },
      ].filter((a) => a.key),
    [],
  );

  return (
    <>
      <div>
        <Tabs defaultActiveKey="users">
          <Tabs.TabPane key="users" tab="Users">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isCompact ? "stretch" : "center",
                flexDirection: isCompact ? "column" : "row",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <SearchBar
                  style={{ width: "100%", margin: 0 }}
                  value={searchQuery.q}
                  searchFields={["name", "email", "language"]}
                  onChange={(e) => {
                    setSearchQuery(e);
                    setCurrentPage(1);
                    updateUrl({ q: e.q || undefined, page: "1" });
                  }}
                  searchType="Enter"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={fcmNotExpired}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setFcmNotExpired(v);
                    setCurrentPage(1);
                    updateUrl({
                      fcmNotExpired: v ? "1" : undefined,
                      page: "1",
                    });
                  }}
                >
                  Only active app (FCM not expired)
                </Checkbox>
                {isCompact && (
                  <Select
                    allowClear
                    placeholder="Sort by version"
                    value={
                      sortField === "installedVersion" ? sortOrder : undefined
                    }
                    onChange={(value) => {
                      setCurrentPage(1);
                      if (value === "asc" || value === "desc") {
                        setSortField("installedVersion");
                        setSortOrder(value);
                        updateUrl({
                          sortField: "installedVersion",
                          sortOrder: value,
                          page: "1",
                        });
                      } else {
                        setSortField(undefined);
                        setSortOrder(undefined);
                        updateUrl({
                          sortField: undefined,
                          sortOrder: undefined,
                          page: "1",
                        });
                      }
                    }}
                    options={[
                      { value: "desc", label: "Newest version" },
                      { value: "asc", label: "Oldest version" },
                    ]}
                    style={{ flex: 1, minWidth: 150 }}
                  />
                )}
              </div>
            </div>
            {isCompact ? (
              <>
                <Spin spinning={loading}>
                  {users.length === 0 && !loading ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No users"
                    />
                  ) : (
                    users.map((record) => {
                      const enabledFeatures = record.betaFeatures
                        ? Object.entries(record.betaFeatures)
                            .filter(([, enabled]) => enabled === true)
                            .map(([featureName]) => featureName)
                        : [];
                      const origin = getLanguageCode(record.originLanguage);
                      const target = getLanguageCode(record.targetLanguage);
                      const location = [record.country, record.city]
                        .filter(Boolean)
                        .join(", ");
                      const lastRun = formatLastRun(record.lastRunTime);
                      return (
                        <div
                          key={record._id}
                          style={{
                            display: "flex",
                            gap: 12,
                            padding: 12,
                            marginBottom: 8,
                            background: record.isDeleted
                              ? "#ffebee"
                              : record.isPremiumUser
                                ? "#fff9c4"
                                : "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                          }}
                        >
                          <Avatar size={56} src={avatarSrc(record.avatar)} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 8,
                                alignItems: "flex-start",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                {canViewUsers ? (
                                  <Link
                                    to={`/panel/users/edit/${record._id}`}
                                    state={record}
                                    style={{
                                      fontWeight: 600,
                                      color: "#1890ff",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {record.name || "-"}
                                  </Link>
                                ) : (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {record.name || "-"}
                                  </span>
                                )}
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: "#888",
                                    marginTop: 2,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {record.email || "-"}
                                </div>
                              </div>
                              {renderUserActions(record)}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                marginTop: 8,
                                alignItems: "center",
                              }}
                            >
                              {record.fcmExpired ? (
                                <Tag color="orange">FCM expired</Tag>
                              ) : (
                                <Tag color="green">FCM OK</Tag>
                              )}
                              {record.isPremiumUser && (
                                <Tag color="gold">Premium</Tag>
                              )}
                              {record.isDeleted && (
                                <Tag color="red">Deleted</Tag>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: "#666",
                                marginTop: 6,
                              }}
                            >
                              {location || "-"} · {origin}\{target}
                              {record.registerDate
                                ? ` · ${new Date(record.registerDate).toLocaleDateString()}`
                                : ""}
                              {lastRun ? (
                                <>
                                  {" · "}
                                  <Tooltip title={lastRun.full}>
                                    <span>Last run {lastRun.short}</span>
                                  </Tooltip>
                                </>
                              ) : null}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px 12px",
                                marginTop: 6,
                                fontSize: 12,
                                color: "#666",
                              }}
                            >
                              <span>
                                DM {record.dailyUsedMessagesCount ?? 0}
                              </span>
                              <span>TM {record.totalMessagesCount ?? 0}</span>
                              <span>
                                DPF {record.dailyPartnerFindCount ?? 0}
                              </span>
                              <span>Ver {record.installedVersion || "-"}</span>
                              <span>Runs {record.runTimes ?? 0}</span>
                              <span>From {record.installedFrom || "-"}</span>
                              <span>
                                FF {record.followingCount ?? 0}/
                                {record.followersCount ?? 0}
                              </span>
                            </div>
                            {enabledFeatures.length > 0 && (
                              <div style={{ marginTop: 6, fontSize: 12 }}>
                                {enabledFeatures.join(", ")}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </Spin>
                {(totalPages > 1 || currentPage > 1) && (
                  <Pagination
                    style={{ marginTop: 8, textAlign: "center" }}
                    size="small"
                    simple
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalCount ?? totalPages * pageSize}
                    onChange={(page, newPageSize) => {
                      setCurrentPage(page);
                      if (newPageSize) setPageSize(newPageSize);
                      updateUrl({
                        page: String(page),
                        ...(newPageSize
                          ? { pageSize: String(newPageSize) }
                          : {}),
                      });
                    }}
                  />
                )}
              </>
            ) : (
              <Table
                loading={loading}
                size="small"
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalCount ?? totalPages * pageSize,
                }}
                rowKey="_id"
                onChange={(pagination, filters, sorter: any) => {
                  const urlUpdates: Record<string, string | undefined> = {};
                  if (pagination?.current) {
                    setCurrentPage(pagination.current);
                    urlUpdates.page = String(pagination.current);
                  }
                  if (pagination?.pageSize) {
                    setPageSize(pagination.pageSize);
                    urlUpdates.pageSize = String(pagination.pageSize);
                  }
                  if (!Array.isArray(sorter) && sorter?.field != null) {
                    const sf = String(sorter.field);
                    const so = sorter.order === "ascend" ? "asc" : "desc";
                    setSortField(sf);
                    setSortOrder(so);
                    urlUpdates.sortField = sf;
                    urlUpdates.sortOrder = so;
                  }
                  if (Object.keys(urlUpdates).length) updateUrl(urlUpdates);
                  fetchUsers(pagination, sorter);
                }}
                onRow={(record) => {
                  let backgroundColor: string | undefined;
                  if (record.isDeleted) {
                    backgroundColor = "#ffebee";
                  } else if (record.isPremiumUser) {
                    backgroundColor = "#fff9c4";
                  }
                  return {
                    style: {
                      backgroundColor,
                    },
                  };
                }}
                columns={columns}
                dataSource={users}
              />
            )}
          </Tabs.TabPane>
          <Tabs.TabPane key="suggestions" tab="Suggestions">
            {isCompact ? (
              <>
                <Spin spinning={suggestionsLoading}>
                  {suggestions.length === 0 && !suggestionsLoading ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No suggestions"
                    />
                  ) : (
                    suggestions.map((row) => {
                      const forUser = suggestionPerson(row.forUserID);
                      const suggested = suggestionPerson(row.suggestedUserID);
                      return (
                        <div
                          key={row._id}
                          style={{
                            padding: 12,
                            marginBottom: 8,
                            background: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <Avatar size={32} src={forUser.avatar} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, color: "#888" }}>
                                For
                              </div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  wordBreak: "break-word",
                                }}
                              >
                                {forUser.label}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              marginTop: 8,
                            }}
                          >
                            <Avatar size={32} src={suggested.avatar} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, color: "#888" }}>
                                Suggested
                              </div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  wordBreak: "break-word",
                                }}
                              >
                                {suggested.label}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 8,
                              marginTop: 8,
                              fontSize: 12,
                              color: "#666",
                            }}
                          >
                            <span>
                              {formatDate(row.createdAt)}
                              {row.scheduledAt
                                ? ` · ${formatDate(row.scheduledAt)}`
                                : ""}
                            </span>
                            <Tag color={row.isProcessed ? "green" : "orange"}>
                              {row.isProcessed ? "Processed" : "Pending"}
                            </Tag>
                          </div>
                        </div>
                      );
                    })
                  )}
                </Spin>
                {suggestionsTotal > suggestionsPageSize && (
                  <Pagination
                    style={{ marginTop: 8, textAlign: "center" }}
                    size="small"
                    simple
                    current={suggestionsCurrentPage}
                    pageSize={suggestionsPageSize}
                    total={suggestionsTotal}
                    onChange={handleSuggestionsPaginationChange}
                  />
                )}
              </>
            ) : (
              <Table
                loading={suggestionsLoading}
                size="small"
                pagination={{
                  current: suggestionsCurrentPage,
                  pageSize: suggestionsPageSize,
                  total: suggestionsTotal,
                  onChange: handleSuggestionsPaginationChange,
                  showSizeChanger: false,
                }}
                columns={suggestionsColumns}
                dataSource={suggestions}
                rowKey="_id"
              />
            )}
          </Tabs.TabPane>
        </Tabs>
      </div>

      <Modal
        title={`Beta Features - ${
          selectedUser?.name || selectedUser?.email || "User"
        }`}
        open={betaFeaturesModalVisible}
        onCancel={() => {
          setBetaFeaturesModalVisible(false);
          setSelectedUser(null);
          setUserBetaFeatures({});
        }}
        onOk={handleSaveBetaFeatures}
        okText="Save"
        cancelText="Cancel"
        width={isCompact ? "100%" : 500}
      >
        {betaFeaturesLoading ? (
          <div>Loading...</div>
        ) : availableFeatures.length === 0 ? (
          <div>
            <p>No beta features configured.</p>
            <p style={{ fontSize: "12px", color: "#999" }}>
              Please add a setting with key "features" containing an array of
              feature names.
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {availableFeatures.map((feature) => (
              <div key={feature} style={{ marginBottom: "12px" }}>
                <Checkbox
                  checked={userBetaFeatures[feature] === true}
                  onChange={(e) =>
                    handleBetaFeatureToggle(feature, e.target.checked)
                  }
                >
                  {feature}
                </Checkbox>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={`Send Notification - ${
          notificationUser?.name || notificationUser?.email || "User"
        }`}
        open={notificationModalVisible}
        onCancel={() => {
          setNotificationModalVisible(false);
          setNotificationUser(null);
          notificationForm.resetFields();
        }}
        onOk={handleSendNotification}
        okText="Send"
        cancelText="Cancel"
        confirmLoading={notificationLoading}
        width={isCompact ? "100%" : 500}
      >
        <Form form={notificationForm} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter notification title" },
            ]}
          >
            <Input placeholder="Enter notification title" />
          </Form.Item>
          <Form.Item
            name="body"
            label="Body"
            rules={[
              { required: true, message: "Please enter notification body" },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Enter notification body" />
          </Form.Item>
          <Form.Item name="notificationType" label="Notification Type">
            <Select placeholder="Select notification type (optional)">
              <Select.Option value={NotificationType.FOLLOW_REQUEST}>
                Follow Request
              </Select.Option>
              <Select.Option value={NotificationType.FOLLOW_ACCEPT}>
                Follow Accept
              </Select.Option>
              <Select.Option value={NotificationType.PARTNER_SUGGESTION}>
                Partner Suggestion
              </Select.Option>
              <Select.Option value={NotificationType.MESSAGE}>
                Message
              </Select.Option>
              <Select.Option value={NotificationType.OTHER}>
                Other
              </Select.Option>
              <Select.Option value={NotificationType.IS_TYPING}>
                Is Typing
              </Select.Option>
              <Select.Option value={NotificationType.SEEN}>Seen</Select.Option>
              <Select.Option value={NotificationType.LOGOUT}>
                Logout
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserListPage;
